import z from 'zod'

import { zodErrorParser } from '@/utils/zod'

import {
  AssetsResponseSchema,
  BaseEntitySchema,
  SocialChannelPlatforms,
  UserDetailsSchema
} from './Common'

export const SystemUserRoles = [
  'ADMIN',
  'ORG_LEAD',
  'DELEGATE',
  'INNER_CIRCLE',
  'INDIVIDUAL'
] as const

export type SystemUserRolesType = typeof SystemUserRoles[number]

export const OrganizationMemberRoles = [
  'ADMIN',
  'ORG_LEAD',
  'DELEGATE'
] as const

// Temporary object without ORG_LEAD for the UI Dropdowns
export const OrganizationMemberRolesWithoutOrgLead = Array.from(OrganizationMemberRoles).filter(role => role !== 'ORG_LEAD')

export type OrganizationMemberRolesType = typeof OrganizationMemberRoles[number]

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IndividualAssignmentTypes = [
  'INNER_CIRCLE',
  'DELEGATE'
] as const

export type IndividualAssignmentTypesType = typeof IndividualAssignmentTypes[number]

// export const FamilyMemberTypes = [
//   'SPOUSE',
//   'CHILD'
// ] as const

// export type FamilyMemberTypesType = typeof FamilyMemberTypes[number]

const AssetsProcessingStatus = [
  'QUEUED',
  'PROCESSING',
  'COMPLETED',
  'REJECTED',
  'FAILED',
  'DELETE_REQUESTED',
  'DELETED',
] as const

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AssignmentStatus = [
  'assigned',
  'unassigned',
] as const

export type AssignmentStatusType = typeof AssignmentStatus[number]

const RoleResponseSchema = BaseEntitySchema.extend({
  id: z.number().int(),
  type: z.enum(SystemUserRoles)
}).strict()

export const OrganizationUserResponseSchema = BaseEntitySchema.extend({
  id: z.string(),
  user: z.union([z.string(), z.any()]).nullish(), // TODO
  organization: z.union([
    z.string(),
    BaseEntitySchema.extend({
      id: z.string(),
      organizationDetails: z.string(),
      owner: z.string(),
    }).strict()
  ]).nullish(),
  roles: z.array(z.union([
    z.enum(SystemUserRoles),
    RoleResponseSchema
  ])).nullish(),
  role: RoleResponseSchema.nullish(),
}).strict()

const OrganizationDetailsResposeSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  name: z.string(),
}).strict()

const OrganizationResponseSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  assets: AssetsResponseSchema.nullish(),
  organizationDetails: OrganizationDetailsResposeSchema,
  owner: z.uuid(),
}).strict()

export type OrganizationResponse = z.infer<typeof OrganizationResponseSchema>

export const zodOrganizationResponseValidator = (
  data: OrganizationResponse
) => zodErrorParser(
  'OrganizationResponse',
  OrganizationResponseSchema,
  data,
)

const OrganizationMemberResponseSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  details: UserDetailsSchema,
  email: z.email(),
  assets: AssetsResponseSchema.nullish(),
  isVerified: z.boolean().nullish(),
  isCognitoConfirmed: z.boolean().nullish(),
  role: z.enum(OrganizationMemberRoles),
  subject: z.union([z.string(), z.object({ id: z.string() })]).nullish(),
  organizationMemberId: z.object({
    id: z.uuid(),
  })
}).strict()

export type OrganizationMemberResponse = z.infer<typeof OrganizationMemberResponseSchema>

export const zodOrganizationMemberResponseValidator = (
  data: OrganizationMemberResponse[]
) => zodErrorParser(
  'OrganizationMemberResponse',
  OrganizationMemberResponseSchema,
  data,
)

const MemberIndividualStatusSchema = BaseEntitySchema.extend({
  firstName: z.string(),
  lastName: z.string().nullable(),
  fullName: z.string(),
  subject: z.object({ id: z.uuid().nullish() }),
  organizationIndividual: z.object({ id: z.uuid().nullish() }),
}).strict()

export type MemberIndividualStatus = z.infer<typeof MemberIndividualStatusSchema>

export const zodMemberIndividualStatusValidator = (
  data: MemberIndividualStatus
) => zodErrorParser(
  'MemberIndividualStatus',
  MemberIndividualStatusSchema,
  data,
)

const IndividualAssignmentResponseSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  details: UserDetailsSchema,
  email: z.email(),
  isVerified: z.boolean().nullish(),
  roles: z.enum([...OrganizationMemberRoles, 'INNER_CIRCLE']).array(),
  subject: z.union([z.string(), z.object({ id: z.string() })]).nullish(),
  assets: AssetsResponseSchema.nullish(),
}).strict()

export type IndividualAssignmentResponse = z.infer<typeof IndividualAssignmentResponseSchema>

export const zodIndividualAssignmentResponseValidator = (
  data: IndividualAssignmentResponse[]
) => zodErrorParser(
  'IndividualAssignmentResponse',
  IndividualAssignmentResponseSchema,
  data,
)

// const IndividualSearchRefinementFieldSchema = BaseEntitySchema.extend({
//   id: z.uuid(),
//   subject: z.object({ id: z.string() }),
//   createdBy: z.object({ id: z.string() }),
//   field: z.string(),
//   value: z.string(),
// }).strict()

// const IndividualSearchRefinementResponseSchema = BaseEntitySchema.extend({
//   HOMETOWN_CITY: z.array(IndividualSearchRefinementFieldSchema).nullish(),
//   HOMETOWN_STATE: z.array(IndividualSearchRefinementFieldSchema).nullish(),
//   HOMETOWN_COUNTRY: z.array(IndividualSearchRefinementFieldSchema).nullish(),
//   LANGUAGE: z.array(IndividualSearchRefinementFieldSchema).nullish(),
//   SPOUSE_NAME: z.array(IndividualSearchRefinementFieldSchema).nullish(),
//   CHILDREN_NAME: z.array(IndividualSearchRefinementFieldSchema).nullish(),
// }).strict()

// export type IndividualSearchRefinementResponse = z.infer<typeof IndividualSearchRefinementResponseSchema>

// export const zodIndividualSearchRefinementResponseValidator = (
//   data: IndividualSearchRefinementResponse
// ) => zodErrorParser(
//   'IndividualSearchRefinementResponse',
//   IndividualSearchRefinementResponseSchema,
//   data,
// )

const IndividualImageSearchRefinementResponseSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  subject: z.object({ id: z.uuid() }),
  createdBy: z.object({ id: z.uuid() }),
  externalId: z.string(),
  source: z.enum(['AWS_S3', 'MOCK']),
  url: z.string(),
  info: z.object({
    status: z.enum(AssetsProcessingStatus),
    reason: z.array(z.string())
  }).strict(),
  metadata: z.object({
    fileName: z.string(),
    mimeType: z.string(),
    sizeInBytes: z.number().int()
  }).strict(),
}).strict()

export type IndividualImageSearchRefinementResponse = z.infer<typeof IndividualImageSearchRefinementResponseSchema>

export const zodIndividualImageSearchRefinementResponseValidator = (
  data: IndividualImageSearchRefinementResponse[]
) => zodErrorParser(
  'IndividualImageSearchRefinementResponse',
  IndividualImageSearchRefinementResponseSchema,
  data,
)

const IndividualSocialPlatformSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  subject: z.object({ id: z.uuid() }),
  type: z.enum(SocialChannelPlatforms),
  url: z.string(),
  trusted: z.boolean(),
  handle: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
}).strict()

export const IndividualSocialPlatformGroupedResponseSchema = z.object({
  instagram: z.array(IndividualSocialPlatformSchema),
  tiktok: z.array(IndividualSocialPlatformSchema),
  X: z.array(IndividualSocialPlatformSchema),
  facebook: z.array(IndividualSocialPlatformSchema),
  linkedIn: z.array(IndividualSocialPlatformSchema),
  snapchat: z.array(IndividualSocialPlatformSchema),
  website: z.array(IndividualSocialPlatformSchema),
  youtube: z.array(IndividualSocialPlatformSchema),
  reddit: z.array(IndividualSocialPlatformSchema),
  other: z.array(IndividualSocialPlatformSchema),
}).strict()

export type IndividualSocialPlatformGroupedResponse = z.infer<typeof IndividualSocialPlatformGroupedResponseSchema>

export const zodIndividualSocialPlatformGroupedResponseValidator = (
  data: IndividualSocialPlatformGroupedResponse
) => zodErrorParser(
  'IndividualSocialPlatformGroupedResponse',
  IndividualSocialPlatformGroupedResponseSchema,
  data,
)

const IndividualAliasResponseSchema = z.object({
  id: z.uuid(),
  alias: z.string(),
  editable: z.boolean().optional(),
}).strict()

export type IndividualAliasResponse = z.infer<typeof IndividualAliasResponseSchema>

export const zodIndividualAliasResponseValidator = (
  data: IndividualAliasResponse[]
) => zodErrorParser(
  'IndividualAliasResponse',
  IndividualAliasResponseSchema,
  data,
)

const ExclusionWordResponseSchema = BaseEntitySchema.extend({
  id: z.uuid(),
  word: z.string(),
  createdBy: z.object({ id: z.uuid() }),
  organization: z.object({ id: z.uuid() }).nullish(),
  organizationIndividual: z.object({ id: z.uuid() }).nullish(),
}).strict()

export type ExclusionWordResponse = z.infer<typeof ExclusionWordResponseSchema>

export const zodExclusionWordResponseValidator = (
  data: ExclusionWordResponse[]
) => zodErrorParser(
  'OrganizationExclusionWordResponse',
  ExclusionWordResponseSchema,
  data,
)
